/**
 * @module builder
 */

import type { PresentationOptionV2, PresentationPageV2, PresentationSectionV2 } from 'paintscout'
import { uuid } from '../../../util'
import { findPage, insertAt, moveWithin, removeAt, replaceAt, withPages } from './locate'

/**
 * CRUD over v2 **pages** — the top level of the tree. A page renders on its own and, when a
 * presentation has more than one, appears as a tab in the customer-facing nav.
 */

export function createPresentationNextPage(args?: {
  label?: string
  sections?: PresentationSectionV2[]
}): PresentationPageV2 {
  const { label = 'New Page', sections = [] } = args ?? {}

  return {
    key: uuid(),
    label,
    title: label,
    active: true,
    advanced: {
      useForNav: true,
      allowCustom: true
    },
    sections
  }
}

export function addPresentationNextPage(args: {
  presentation: PresentationOptionV2
  page: PresentationPageV2
  /** Defaults to appending. */
  index?: number
}): PresentationOptionV2 {
  const { presentation, page, index } = args

  return withPages(presentation, insertAt(presentation.pages ?? [], index ?? Number.MAX_SAFE_INTEGER, page))
}

export function updatePresentationNextPage(args: {
  presentation: PresentationOptionV2
  page: PresentationPageV2
}): PresentationOptionV2 {
  const { presentation, page } = args
  const location = findPage(presentation, page.key)

  if (!location) {
    return presentation
  }

  return withPages(presentation, replaceAt(presentation.pages, location.pageIndex, page))
}

/**
 * Deleting the last remaining page would leave a presentation with nothing to render, so it's a
 * no-op — the editor disables the action, and this is the backstop.
 */
export function deletePresentationNextPage(args: {
  presentation: PresentationOptionV2
  pageKey: string
}): PresentationOptionV2 {
  const { presentation, pageKey } = args
  const location = findPage(presentation, pageKey)

  if (!location || presentation.pages.length <= 1) {
    return presentation
  }

  return withPages(presentation, removeAt(presentation.pages, location.pageIndex))
}

/** Copies a page and everything under it, with fresh keys all the way down. */
export function copyPresentationNextPage(args: {
  presentation: PresentationOptionV2
  pageKey: string
}): PresentationOptionV2 {
  const { presentation, pageKey } = args
  const location = findPage(presentation, pageKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex } = location

  return withPages(presentation, insertAt(presentation.pages, pageIndex + 1, copyPageWithNewKeys(page)))
}

/**
 * Re-keys a page and its whole subtree. Exported because pasting a page copied in another
 * presentation (the editor keeps one on `sessionStorage`) needs the same treatment — reusing the
 * original keys would collide with the source presentation if both end up on the same quote.
 */
export function copyPageWithNewKeys(page: PresentationPageV2): PresentationPageV2 {
  return {
    ...page,
    key: uuid(),
    label: page.label,
    sections: (page.sections ?? []).map((section) => ({
      ...section,
      key: uuid(),
      blocks: (section.blocks ?? []).map((block) => ({ ...block, key: uuid() }))
    }))
  }
}

export function reorderPresentationNextPages(args: {
  presentation: PresentationOptionV2
  fromIndex: number
  toIndex: number
}): PresentationOptionV2 {
  const { presentation, fromIndex, toIndex } = args
  const pages = presentation?.pages ?? []

  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= pages.length) {
    return presentation
  }

  return withPages(presentation, moveWithin(pages, fromIndex, toIndex))
}
